import { Customer } from './../../../core/models/customer.model';
import { Company } from './../../../core/models/company.model';
import { Jobsite } from './../../../core/models/jobsite.model';
import { PaymentProcessActions, PaymentProcessActionTypes } from './../actions/payment-process.action';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { PaymentDocument } from './../../../components/bulk-payment/models/payment-document.model';
import { PaymentMethod } from './../../models/payment-method';
import { PaymentNetwork } from './../../models/payment-network';
import { PayerBrokerResponse } from './../../models/payer-broker-response';
import { PaymentHeader } from './../../models/payment-header';
import { PaymentDetail } from './../../models/payment-detail';
import { IExternalAppData } from '../../../core/models/externalAppData.model';

export interface State extends EntityState<PaymentDocument> {
    customer: string;
    company: string;
    jobsite: string;
    paymentMethod: string;
    networking: string;
    networkingDetail: string;
    referenceCode: string;
    payerInfo: PayerBrokerResponse;
    paymentHeader: PaymentHeader;
    paymentExternalData: IExternalAppData;
    isProcessingReference: boolean;
    isProcessingNetwork: boolean;
    salesOrderNumber: string;
}

const adapter: EntityAdapter<PaymentDocument> = createEntityAdapter<PaymentDocument>({
    selectId: (document: PaymentDocument) => document.documentId,
    sortComparer: false
});

const initialState: State = adapter.getInitialState({
    customer: undefined,
    company: undefined,
    jobsite: undefined,
    paymentMethod: undefined,
    networking: undefined,
    networkingDetail: undefined,
    referenceCode: undefined,
    payerInfo: undefined,
    paymentHeader: undefined,
    paymentExternalData: undefined,
    isProcessingReference: false,
    isProcessingNetwork: false,
    salesOrderNumber: ''
});

export function reducer(state = initialState, action: PaymentProcessActions): State {
    switch (action.type) {
        case PaymentProcessActionTypes.START_PAYMENT_PROCESS: {
            return initialState;
        }

        case PaymentProcessActionTypes.SELECT_CUSTOMER: {
            return { ...state, customer: action.payload };
        }

        case PaymentProcessActionTypes.SELECT_COMPANY: {
            return { ...state, company: action.payload };
        }

        case PaymentProcessActionTypes.SELECT_JOBSITE: {
            return { ...state, jobsite: action.payload };
        }

        case PaymentProcessActionTypes.SELECT_PAYMENT_METHOD: {
            return { ...state, paymentMethod: action.payload };
        }

        case PaymentProcessActionTypes.SELECT_NETWORKING: {
            return { ...state, networking: action.payload };
        }

        case PaymentProcessActionTypes.SELECT_NETWORKING_DETAIL: {
            return { ...state, networkingDetail: action.payload };
        }

        case PaymentProcessActionTypes.SET_PAYMENT_HEADER: {
            return { ...state, paymentHeader: action.payload };
        }

        case PaymentProcessActionTypes.SET_PAYMENT_EXTERNAL_DATA: {
            return { ...state, paymentExternalData: action.payload };
        }

        case PaymentProcessActionTypes.ADD_PAYMENT_DOCUMENTS: {
            return adapter.addMany(action.payload, state);
        }

        case PaymentProcessActionTypes.UPDATE_PAYMENT_DOCUMENTS: {
            return adapter.updateMany(action.payload, state);
        }

        case PaymentProcessActionTypes.REMOVE_PAYMENT_DOCUMENTS: {
            return adapter.removeAll(state);
        }

        case PaymentProcessActionTypes.UPDATE_PAYMENT_DOCUMENT: {
            return adapter.updateOne(action.payload, state);
        }

        case PaymentProcessActionTypes.REMOVE_PAYMENT_DOCUMENT: {
            return adapter.removeOne(action.payload, state);
        }

        case PaymentProcessActionTypes.CREATE_REFERENCE_PAYMENT: {
            return { ...state, isProcessingReference: true };
        }

        case PaymentProcessActionTypes.CREATE_REFERENCE_PAYMENT_SUCCESS: {
            return { ...state, referenceCode: action.payload };
        }

        case PaymentProcessActionTypes.GET_PAYER_INFORMATION_SUCCESS: {
            return { ...state, payerInfo: action.payload };
        }

        case PaymentProcessActionTypes.CALL_TO_NETWORKING: {
            return { ...state, isProcessingNetwork: true };
        }

        case PaymentProcessActionTypes.UPDATE_SALES_ORDER_NUMBER: {
            return { ...state, salesOrderNumber: action.payload };
        }

        default: {
            return state;
        }
    }
}

export const getSelectedCustomer = (state: State) => state && state.customer;
export const getSelectedCompany = (state: State) => state && state.company;
export const getSelectedJobsite = (state: State) => state && state.jobsite;
export const getSelectPaymentMethod = (state: State) => state && state.paymentMethod;
export const getSelectNetworking = (state: State) => state && state.networking;
export const getSelectNetworkingDetail = (state: State) => state && state.networkingDetail;
export const getPaymentHeader = (state: State) => state && state.paymentHeader;
export const getPaymentExternalData = (state: State) => state && state.paymentExternalData;
export const getPaymentDocuments = (state: State) => state && state.entities;
export const getReferenceCode = (state: State) => state && state.referenceCode;
export const getPayerInformation = (state: State) => state && state.payerInfo;
export const isProcessingReference = (state: State) => state && state.isProcessingReference;
export const isProcessingNetwork = (state: State) => state && state.isProcessingNetwork;
export const getSalesOrderNumber = (state: State) => state && state.salesOrderNumber;
